// Lecture d'un CV. Trois temps dans une seule couche : on dépose, le modèle lit, un
// humain relit. Le troisième temps est le seul qui compte — c'est là qu'on rattrape
// une date fausse pour une minute, au lieu de la traîner dans tous les rapprochements.
import { api, direRefus } from '../api.ts';
import { $, blocAttente, brut, compteur, h, mot, peindre, valeur, type Brut } from '../ui.ts';
import { type Referentiel } from '../etat.ts';
import { FICHE_VIDE, brancherFiche, formulaireProfil, fusionnerFiche, recolterProfil,
         type FicheProfil } from './fiche.ts';
interface Experience {
  intitule: string; client?: string | null; secteur?: string | null;
  debut?: string | null; fin?: string | null; resume: string;
  // Ce que la personne a obtenu à ce poste, et avec quoi. C'est la matière du CV WAKEA :
  // le résumé dit ce qu'elle faisait, la réalisation dit ce qu'elle a changé.
  realisations?: string[]; environnement?: string[];
}
interface Fiche extends FicheProfil {
  experiences: Experience[]; deduits: string[]; manques: string[];
}

/** Ce que le serveur renvoie toujours, même quand le CV ne dit presque rien. */
const PROPOSITION_VIDE: Fiche = { ...FICHE_VIDE, experiences: [], deduits: [], manques: [] };

const cadre = (titre: string, corps: Brut, pied: Brut): Brut => h`
  <div class="voile" data-act="fermer-voile"><div class="couche large-c">
    <div class="ct"><div><div class="sur">LECTURE D'UN CV</div><h2>${titre}</h2></div>
      <button class="x" data-act="fermer" aria-label="Fermer">✕</button></div>
    <div class="cc">${corps}</div>
    <div class="cp">${pied}</div>
  </div></div>`;

/** Un champ déduit n'est pas un champ faux ; c'est un champ à regarder en premier. */
const etiquette = (libelle: string, cle: string, deduits: string[]) => h`
  <label class="lab" for="x-${cle}">${libelle}
    ${deduits.includes(cle) ? h`<span class="pt" style="color:var(--copper);letter-spacing:0">· déduit</span>` : ''}</label>`;

const champ = (libelle: string, cle: string, v: unknown, deduits: string[], type = 'text') => h`
  <div>${etiquette(libelle, cle, deduits)}
    <input class="champ" id="x-${cle}" type="${type}" value="${v ?? ''}"></div>`;

const listeChamp = (libelle: string, cle: string, v: string[], deduits: string[]) =>
  champ(libelle, cle, v.join(', '), deduits);

const experience = (x: Experience, i: number) => h`
  <div class="carte" style="margin-top:9px">
    <div style="display:grid;grid-template-columns:2fr 1fr;gap:9px">
      <input class="champ" data-e="intitule" data-i="${i}" value="${x.intitule}" placeholder="Intitulé">
      <input class="champ" data-e="client" data-i="${i}" value="${x.client ?? ''}" placeholder="Client">
    </div>
    <div class="rang" style="gap:9px;margin-top:7px">
      <input class="champ" data-e="debut" data-i="${i}" type="date" value="${x.debut ?? ''}" style="max-width:160px">
      <input class="champ" data-e="fin" data-i="${i}" type="date" value="${x.fin ?? ''}" style="max-width:160px">
      <input class="champ" data-e="secteur" data-i="${i}" value="${x.secteur ?? ''}" placeholder="Secteur">
    </div>
    <textarea class="champ" data-e="resume" data-i="${i}" rows="3" style="margin-top:7px">${x.resume}</textarea>
    <div style="display:grid;grid-template-columns:3fr 2fr;gap:9px;margin-top:7px">
      <div><label class="lab">RÉALISATIONS · UNE PAR LIGNE</label>
        <textarea class="champ" data-e="realisations" data-i="${i}" rows="3"
          placeholder="Ce qui a été obtenu, chiffré si le CV le chiffre">${(x.realisations ?? []).join('\n')}</textarea></div>
      <div><label class="lab">ENVIRONNEMENT · UN PAR LIGNE</label>
        <textarea class="champ" data-e="environnement" data-i="${i}" rows="3"
          placeholder="Les outils du poste">${(x.environnement ?? []).join('\n')}</textarea></div>
    </div>
  </div>`;

/**
 * L'état de la lecture en cours. Il vit au niveau du module, et non dans la fonction
 * d'ouverture : l'écouteur ci-dessous est posé une seule fois pour toute la durée de la
 * page, et deux ouvertures successives ne doivent pas laisser deux écouteurs derrière
 * elles — ils enverraient chacun leur requête.
 */
interface Lecture {
  ref: Referentiel; fini: (id: string) => void;
  fichier: File | null; traitement: string; empreinte: string;
  /** Renseigné quand on complète un dossier qui existe au lieu d'en créer un. */
  profil: string | null;
  /**
   * La clé d'idempotence de la fiche en train d'être saisie. Elle naît avec la couche et
   * ne change plus : deux envois qui la portent décrivent la même intention de créer.
   */
  cle: string;
}
let vivante: Lecture | null = null;
let ecoute = false;

/**
 * Un geste à la fois. Le verrou écarte les clics tant que l'envoi précédent est en vol —
 * c'est ce qui manquait au brief de mission, où trois clics ont créé trois missions.
 */
let occupe = false;

export function ouvrirExtraction(ref: Referentiel, fini: (id: string) => void): void {
  vivante = { ref, fini, fichier: null, traitement: '', empreinte: '', profil: null,
              cle: crypto.randomUUID() };
  poserEcoute();
  depot();
}

/**
 * Lire le CV déjà au dossier. C'est le chemin d'une candidature venue du site : la
 * personne a déposé son CV elle-même, et la lecture reste un geste que quelqu'un décide.
 * Pas d'étape de dépôt, donc, mais la même relecture — c'est elle qui compte.
 */
export async function ouvrirPrefill(
  ref: Referentiel, profilId: string, documentId: string, nomDocument: string,
  fini: (id: string) => void,
): Promise<void> {
  vivante = { ref, fini, fichier: null, traitement: '', empreinte: '', profil: profilId,
              cle: crypto.randomUUID() };
  poserEcoute();
  const arreter = attente(nomDocument);
  try {
    // Le dossier d'abord, la lecture ensuite. Le formulaire doit montrer ce qu'il va
    // écrire : s'il s'ouvrait vide, un taux journalier ou un téléphone déjà au dossier
    // repartirait à blanc sans que personne ne le voie passer.
    const { profil } = await api.get<{ profil: Record<string, unknown> }>(`/api/profils/${profilId}`);
    const r = await api.post<{ traitement: string; cout_eur: number; fiche: Fiche; tronquee: boolean }>(
      `/api/extraction/document/${documentId}`);
    vivante.traitement = r.traitement;
    arreter();
    relecture({ ...PROPOSITION_VIDE, ...r.fiche,
                ...fusionnerFiche(profil, r.fiche as unknown as Record<string, unknown>) },
              r.cout_eur, r.tronquee);
  } catch (e) {
    arreter();
    peindre('#couche', cadre('Lecture impossible',
      h`<div class="enc ar"><span class="sur">LE CV N'A PAS PU ÊTRE LU</span>
          <p>${direRefus(e)}</p></div>
        <p class="sm sourd">Le document reste au dossier. Vous pouvez compléter la fiche à la main
           depuis « Modifier la fiche ».</p>`,
      h`<button class="bt pousse" data-act="fermer">Fermer</button>`));
  }
}

// ─── 1. Dépôt ───────────────────────────────────────────────────────────────

function depot(): void {
  peindre('#couche', cadre('Ajouter un profil à partir de son CV', h`
    <div class="enc gl"><span class="sur">CE QUE FAIT MANA, ET CE QU'ELLE NE FAIT PAS</span>
      <p>Le modèle lit le document et <b>propose</b> une fiche. Il ne crée rien : tant que vous
         n'avez pas validé, ni le nom ni les coordonnées de cette personne n'entrent en base.
         Vous relirez chaque champ, et ceux qu'il aura déduits plutôt que lus vous seront signalés.</p></div>

    <div><label class="lab" for="x-fichier">LE CV · PDF, WORD .DOCX, .TXT OU .MD — 15 Mo AU PLUS</label>
      <div class="depose" id="x-zone">
        <span class="sm sourd">Glissez le CV ici, ou choisissez-le :</span>
        <input class="champ" type="file" id="x-fichier" accept=".pdf,.docx,.txt,.md" required>
      </div>
      <p class="pt sourd" style="margin:5px 0 0">Un CV scanné, pris en photo, ou enregistré en
        Word d'avant 2007 (.doc) ne se lit pas : il faut un PDF dont le texte se sélectionne,
        ou un .docx. Ces formats sont refusés au dépôt plutôt que rangés sans pouvoir servir.</p></div>

    <p class="err" id="err" hidden></p>`,
    h`<button class="bt" data-act="fermer">Annuler</button>
      <button class="bt" data-x="main">Remplir à la main</button>
      <button class="bt p" data-x="lire">Lire le CV</button>`));
  brancherDepot();
}

/**
 * Le glisser-déposer. La cible est toute la couche, pas seulement le cadre : viser un
 * rectangle de cent pixels avec un fichier au bout du curseur est une exigence inutile.
 * Les écouteurs se reposent à chaque rendu — les nœuds sont neufs à chaque fois.
 */
function brancherDepot(): void {
  const couche = $('.couche');
  const zone = $('#x-zone');
  const champ = $('#x-fichier') as HTMLInputElement | null;
  if (!couche || !zone || !champ) return;

  const marquer = (dessus: boolean) => zone.classList.toggle('sur', dessus);

  for (const nom of ['dragenter', 'dragover'] as const)
    couche.addEventListener(nom, (ev) => { ev.preventDefault(); marquer(true); });
  for (const nom of ['dragleave', 'dragend'] as const)
    couche.addEventListener(nom, (ev) => { ev.preventDefault(); if (ev.target === couche) marquer(false); });

  couche.addEventListener('drop', (ev) => {
    ev.preventDefault();
    marquer(false);
    const fichiers = (ev as DragEvent).dataTransfer?.files;
    if (!fichiers?.length) return;
    champ.files = fichiers;
    champ.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

// ─── 2. Lecture ─────────────────────────────────────────────────────────────

function attente(nom?: string): () => void {
  peindre('#couche', cadre('Lecture en cours',
    blocAttente('LE MODÈLE LIT LE DOCUMENT',
      "La fiche proposée s'affichera ici, champ par champ. Rien ne sera écrit avant que vous ne l'ayez relue."),
    h`<span class="sm sourd pousse">${nom ?? vivante?.fichier?.name ?? ''}</span>`));
  return compteur('#chrono');
}

// ─── 3. Relecture ───────────────────────────────────────────────────────────

function relecture(f: Fiche, cout: number | null, tronquee = false): void {
  const ref = vivante!.ref;
  const complete = Boolean(vivante!.profil);
  peindre('#couche', cadre(`${f.prenom} ${f.nom}`.trim() || 'Fiche proposée', h`
    ${f.manques.length ? h`<div class="enc ar"><span class="sur">CE QUE LE CV NE DIT PAS</span>
      <ul style="margin:6px 0 0;padding-left:17px">${f.manques.map((m) =>
        h`<li style="font-size:12.5px;line-height:1.5">${m}</li>`)}</ul></div>` : ''}

    ${tronquee ? h`<div class="enc ar"><span class="sur">LECTURE INTERROMPUE</span>
      <p>Le CV était plus long que ce que le modèle pouvait rendre d'un coup. Les dernières
         expériences manquent peut-être — vérifiez-les avant de créer la fiche.</p></div>` : ''}

    ${complete ? h`<div class="enc gl"><span class="sur">VOUS COMPLÉTEZ UN DOSSIER QUI EXISTE</span>
      <p>Les champs ci-dessous sont <b>ceux du dossier</b>, recouverts par ce que le document
         dit effectivement. Ce qu'il ne dit pas — un taux journalier, une rémunération, une
         date de disponibilité, un téléphone — reste tel qu'il est : rien ne s'efface au
         passage. Ce que vous lisez ici est exactement ce qui sera enregistré.</p>
      <p>Les expériences relues, elles, remplacent celles du dossier : deux lectures du même
         parcours ne doivent pas laisser deux jeux qui se ressemblent. D'où vient ce dossier,
         sous quel régime il tombe et qui porte la relation ne changent pas — une lecture de
         document n'en sait rien.</p></div>` : ''}

    ${formulaireProfil(f, ref, f.deduits, !complete)}

    <div class="filet" style="margin-top:6px"><span class="sur">EXPÉRIENCES · ${f.experiences.length}</span>
      <span class="pt sourd">ce sont elles que le rapprochement lira, une par une</span></div>
    ${f.experiences.map(experience)}

    <p class="err" id="err" hidden></p>`,
    h`<span class="sm sourd pousse">${cout == null ? 'Saisie manuelle'
        : `Lecture : ${cout.toFixed(3).replace('.', ',')} €`}</span>
      <button class="bt" data-act="fermer">Abandonner</button>
      <button class="bt p" data-x="creer">${complete ? 'Compléter la fiche' : 'Créer la fiche'}</button>`));

  brancherFiche();
  vivante!.empreinte = JSON.stringify(recolter(f.experiences.length));
}

/** Relit la couche telle qu'elle est à l'écran. Une seule lecture, donc une seule vérité. */
function recolter(nombre: number) {
  const dans = (i: number, nom: string) =>
    ($(`[data-e="${nom}"][data-i="${i}"]`) as HTMLInputElement | HTMLTextAreaElement | null)?.value.trim() ?? '';

  const lignes = (i: number, nom: string) =>
    dans(i, nom).split('\n').map((x) => x.trim()).filter(Boolean);

  return {
    ...recolterProfil(),
    experiences: Array.from({ length: nombre }, (_, i) => ({
      intitule: dans(i, 'intitule'), client: dans(i, 'client') || null, secteur: dans(i, 'secteur') || null,
      debut: dans(i, 'debut') || null, fin: dans(i, 'fin') || null, resume: dans(i, 'resume'),
      realisations: lignes(i, 'realisations'), environnement: lignes(i, 'environnement'),
    })).filter((x) => x.intitule && x.resume),
  };
}

const dire = (message: string) => { const n = $('#err'); if (n) { n.textContent = message; n.hidden = false; } };

function poserEcoute(): void {
  if (ecoute) return;
  ecoute = true;

  $('#couche')?.addEventListener('click', async (ev) => {
    const cible = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-x]');
    const geste = cible?.dataset['x'];
    if (!geste || !vivante || occupe) return;

    if (geste === 'main') {
      // Le même formulaire, vide : inutile d'en entretenir un second pour la saisie.
      vivante.fichier = ($('#x-fichier') as HTMLInputElement | null)?.files?.[0] ?? null;
      return relecture(PROPOSITION_VIDE, null);
    }

    if (geste === 'lire') {
      vivante.fichier = ($('#x-fichier') as HTMLInputElement | null)?.files?.[0] ?? null;
      if (!vivante.fichier) return dire('Choisissez un document.');
      const fichier = vivante.fichier;
      const arreter = attente();
      occupe = true;
      const forme = new FormData();
      forme.set('fichier', fichier);
      try {
        const r = await api.post<{ traitement: string; cout_eur: number; fiche: Fiche; tronquee: boolean }>(
          '/api/extraction/cv', forme);
        vivante.traitement = r.traitement;
        arreter();
        relecture({ ...PROPOSITION_VIDE, ...r.fiche }, r.cout_eur, r.tronquee);
      } catch (e) { arreter(); depot(); dire(direRefus(e)); }
      finally { occupe = false; }
    }

    if (geste === 'creer') {
      const nombre = document.querySelectorAll('[data-e="intitule"]').length;
      const { experiences, ...fiche } = recolter(nombre);
      if (!fiche.prenom || !fiche.nom) return dire('Le prénom et le nom sont obligatoires.');

      // Dossier existant : on complète, on ne crée pas. Le document est déjà au dossier.
      if (vivante.profil) {
        occupe = true;
        const suite = JSON.stringify({ experiences, ...fiche }) === vivante.empreinte ? 'accepte' : 'modifie';
        const { origine: _o, collecte: _c, ...sansRegime } = fiche as Record<string, unknown>;
        try {
          await api.remplacer(`/api/extraction/profil/${vivante.profil}`,
            { ...sansRegime, experiences, traitement: vivante.traitement, suite });
          const fini = vivante.fini;
          const id = vivante.profil;
          vivante = null;
          mot('Fiche complétée — relue et validée par vous.');
          fini(id);
        } catch (e) { dire(direRefus(e)); }
        finally { occupe = false; }
        return;
      }

      const bouton = cible as HTMLButtonElement;
      const libelle = bouton.textContent;
      occupe = true;
      bouton.disabled = true;
      bouton.textContent = 'Enregistrement…';
      try {
        // Sans CV lu, c'est une saisie ordinaire : pas de document à joindre, pas de
        // lecture dont rendre compte. Le formulaire est le même, la route ne l'est pas.
        const cree = vivante.fichier && vivante.traitement
          ? await (async () => {
              const suite = JSON.stringify({ experiences, ...fiche }) === vivante!.empreinte ? 'accepte' : 'modifie';
              const forme = new FormData();
              forme.set('fiche', JSON.stringify({ ...fiche, experiences, traitement: vivante!.traitement, suite }));
              forme.set('fichier', vivante!.fichier!);
              return api.post<{ id: string }>('/api/extraction/profil', forme);
            })()
          : await api.post<{ id: string }>('/api/profils', { ...fiche, cle_creation: vivante.cle });

        // Un CV choisi puis saisi à la main reste un CV : le perdre en silence serait
        // pire que de ne pas l'avoir proposé.
        if (vivante.fichier && !vivante.traitement) {
          const forme = new FormData();
          forme.set('categorie', 'cv');
          forme.set('fichier', vivante.fichier);
          await api.post(`/api/profils/${cree.id}/documents`, forme).catch(() => mot(
            "Fiche créée, mais le CV n'a pas pu être joint — redéposez-le depuis la fiche."));
        }

        const fini = vivante.fini;
        vivante = null;
        mot('Fiche créée — elle reste à qualifier.');
        fini(cree.id);
      } catch (e) {
        // L'échec rend la main : rien n'a été créé, et la même clé servira au prochain
        // essai — c'est ce qui permet de corriger un champ sans risquer deux dossiers.
        bouton.disabled = false;
        bouton.textContent = libelle;
        dire(direRefus(e));
      } finally { occupe = false; }
    }
  });
}
